import { registerSW } from "virtual:pwa-register";
import { logger } from "./functions/logger";
import { SW_UPDATE_INTERVAL_MS } from "./config";

// Explicit SW registration so we can poll for new versions on a timer. Without this
// the SW only re-checks on page (re)load, so a left-open or stuck (white-screen) tab
// keeps running the old build once it's back online. registerType is "autoUpdate", so
// a detected update installs, skips waiting, and reloads the page automatically.
//
// The console logs below are intentional (temporary) so update behaviour is observable
// in production while we validate the rollout. Remove/quiet them once confirmed working.
export default function registerServiceWorker() {
  registerSW({
    immediate: true,
    onRegisteredSW(swUrl, r) {
      console.log("[PWA] SW registered:", swUrl, "| controller:", !!navigator.serviceWorker.controller);
      if (!r) return;

      // Observe whether update() actually discovers a new worker.
      r.addEventListener("updatefound", () => {
        const nw = r.installing;
        console.log("[PWA] updatefound → new worker installing:", nw?.state);
        nw?.addEventListener("statechange", () => {
          console.log("[PWA] new worker state:", nw.state);
          // When the new worker is installed AND there was already a controller,
          // this is an update (not first install). autoUpdate should skipWaiting +
          // reload; log so we can see if it doesn't.
          if (nw.state === "installed" && navigator.serviceWorker.controller) {
            console.log("[PWA] update installed — expecting auto skipWaiting + reload");
            // The page is about to reload itself, mid-work, with no warning. A
            // collector reports that as "the app closed / logged me out", and up
            // to now it existed only in a console nobody can read. Pairing this
            // timestamp with her complaint answers it in one query.
            //
            // Best-effort: the reload may win the race against this write, so a
            // missing record is not proof the reload did not happen. A present
            // one is proof that it did.
            logger.warning("app.auto_reload", "New build installed; app is reloading itself", {
              entity: "app",
              context: { swUrl, path: window.location.pathname },
            });
          }
        });
      });

      setInterval(async () => {
        if (r.installing || !navigator) return;
        if ("connection" in navigator && !navigator.onLine) {
          console.log("[PWA] update check skipped (offline)");
          return;
        }
        try {
          const resp = await fetch(swUrl, {
            cache: "no-store",
            headers: { cache: "no-store", "cache-control": "no-cache" },
          });
          console.log("[PWA] update check → server sw.js status:", resp?.status);
          if (resp?.status === 200) {
            await r.update();
            console.log(
              "[PWA] update() done → waiting:",
              !!r.waiting,
              "| installing:",
              !!r.installing
            );
          }
        } catch (e) {
          console.log("[PWA] update check failed (offline/unreachable):", e?.message);
        }
      }, SW_UPDATE_INTERVAL_MS);
    },
    onNeedRefresh() {
      console.log("[PWA] onNeedRefresh (new content available)");
    },
    onOfflineReady() {
      console.log("[PWA] offline ready");
    },
    onRegisterError(error) {
      console.error("[PWA] SW registration error", error);
    },
  });
}
